import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { parseCityLandingPath, type SEOLocale } from '@/lib/seo/locale-seo';

/** Déduit la locale SEO depuis le pathname (URL réelle, pas cookie i18n). */
export const resolveSeoLocaleFromPathname = (pathname: string): SEOLocale | null => {
  const city = parseCityLandingPath(pathname);
  if (city) return city.locale;
  if (pathname.startsWith('/en/')) return 'en';
  if (pathname.startsWith('/ar/')) return 'ar';
  if (pathname.startsWith('/fr/location-voiture')) return 'fr';
  return null;
};

/**
 * Aligne i18n + dir/html lang avec l’URL SEO courante (/en/..., /ar/..., /location-voiture/...).
 */
export const useSeoRouteLocale = (): SEOLocale | null => {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();

  const routeLocale = useMemo(() => resolveSeoLocaleFromPathname(pathname), [pathname]);

  useEffect(() => {
    if (!routeLocale) return;
    if (i18n.language?.split('-')[0] !== routeLocale) {
      void i18n.changeLanguage(routeLocale);
    }
    document.documentElement.lang = routeLocale;
    document.documentElement.dir = routeLocale === 'ar' ? 'rtl' : 'ltr';
  }, [routeLocale, i18n]);

  return routeLocale;
};
